import { ReactNode, useEffect, useRef, useId, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { useBlockingOverlay } from '../../hooks/canvasShortcutGuard'
import { createDebug } from '../../utils/debugLog'

const log = createDebug('dw:ui:modal')

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'settings' | 'full'
  /**
   * Body padding. Default `true` keeps the standard `p-5`. Set `false` for a
   * dialog whose child owns the full bleed (e.g. the settings shell's
   * nav-column / panel split, which must reach the rounded edges).
   */
  padded?: boolean
  /**
   * Suppress the built-in top-right close button for dialogs that render their
   * own close affordance in a custom header.
   */
  hideCloseButton?: boolean
  /**
   * Replace the default surface styling (background, radius, border). The
   * layout/position classes are always applied. Use only when a design calls
   * for chrome the shared token set doesn't cover.
   */
  surfaceClassName?: string
  /**
   * Extra classes for the full-screen overlay. Use this for explicit layering
   * requirements when a modal opens from another full-screen surface.
   */
  overlayClassName?: string
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-none w-[calc(100vw-2rem)]',
  // Fixed-width split dialog (Figma "Settings" = 800x600). Width is capped
  // rather than fixed so the dialog still collapses on small viewports.
  settings: 'max-w-[800px]',
}

const DEFAULT_SURFACE = 'bg-surface-1 rounded-lg border border-border'

export default function Modal({
  isOpen,
  onClose,
  children,
  title,
  size = 'lg',
  padded = true,
  hideCloseButton = false,
  surfaceClassName,
  overlayClassName,
}: ModalProps) {
  const { t } = useTranslation('shared')
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<Element | null>(null)
  const titleId = useId()
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // While this modal is open it covers the canvas — suspend canvas keyboard
  // shortcuts (Delete/Backspace node deletion, etc.). Covers every <Modal>-based
  // overlay in one place. See canvasShortcutGuard.
  useBlockingOverlay(isOpen)

  const handleClose = useCallback(() => {
    onCloseRef.current()
  }, [])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      // SHORTCUT_ID: canvas.closeModal — keep keyboardShortcuts.ts in sync (Keyboard Shortcuts modal).
      if (e.key === 'Escape') handleClose()
    }

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialogRef.current) return

      const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement?.focus()
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement?.focus()
      }
    }

    if (isOpen) {
      previousActiveElement.current = document.activeElement
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('keydown', handleTab)
      document.body.style.overflow = 'hidden'
      log('open title=%s size=%s mounted=%s', title ?? '(untitled)', size, 'portal:document.body')
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('keydown', handleTab)
      document.body.style.overflow = ''
      if (previousActiveElement.current instanceof HTMLElement) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen, handleClose, title, size])

  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/70 overflow-y-auto animate-fade-in',
        overlayClassName,
      )}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="presentation"
      data-testid="modal-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={cn(
          'relative w-full overflow-hidden animate-scale-in',
          surfaceClassName ?? DEFAULT_SURFACE,
          sizeClasses[size]
        )}
      >
        {!hideCloseButton && (
          <button
            onClick={handleClose}
            className={cn(
              'absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-md',
              'bg-transparent hover:bg-surface-2 transition-colors',
              'text-muted-foreground hover:text-foreground cursor-pointer',
              // Keyboard users need a visible target; this control is often
              // the first focusable element in the dialog.
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
            )}
            aria-label={t('modal.closeDialog')}
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {title && (
          <div className="px-5 py-4 border-b border-border">
            <h2 id={titleId} className="text-sm font-medium text-foreground">{title}</h2>
          </div>
        )}

        <div className={cn(size === 'full' ? 'h-[calc(100vh-6rem)] overflow-hidden' : 'max-h-[85vh] overflow-y-auto', padded && 'p-5')}>{children}</div>
      </div>
    </div>,
    document.body
  )
}
